'use client';
import { useMemo } from 'react';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { DATA_KEYS } from '@/consts/dataKeys';
import FLOW_TYPES from '@/services/funnelService/consts/flowType';
import { type AvailableTimeSlots } from '@/services/liveConsultationService/types';
import {
  type OptionsType,
  generateDateOptions,
  generateTimeOptions,
} from '@/services/liveConsultationService/utils';

import ConsultationCard from './components/consultationCard';
import ResubscriptionConsultation from './components/flows/resubscription';
import SignupConsultation from './components/flows/signup';
import { useDataProvider } from '../../components/DataProvider/useDataProvider';

const ConsultationForm = ({
  flowType,
  options,
}: {
  flowType: valueof<typeof FLOW_TYPES>;
  options: {
    availableTimeSlots: AvailableTimeSlots;
    dateOptions: OptionsType;
    timeOptions: OptionsType;
  };
}) => {
  switch (flowType) {
    case FLOW_TYPES.Resubscription:
    case FLOW_TYPES.ResubscriptionNonExpedited:
      return <ResubscriptionConsultation {...options} />;
    default:
      return <SignupConsultation {...options} />;
  }
};

export const ScheduleConsultation = () => {
  const { availableTimeSlots, scheduledConsultation } = useDataProvider(
    DATA_KEYS.availableTimeSlots,
    DATA_KEYS.scheduledConsultation,
  );
  const { flowType } = useFlowContext();

  const dateOptions = useMemo(() => {
    return generateDateOptions(
      Object.keys(availableTimeSlots.timeSlots)
        .map((timestamp) => Number.parseInt(timestamp))
        .sort(),
      availableTimeSlots.timezone,
    );
  }, [availableTimeSlots]);

  const timeOptions = useMemo(() => {
    if (dateOptions.length === 0) {
      return [];
    }

    return generateTimeOptions(
      availableTimeSlots.timeSlots[
        scheduledConsultation?.scheduledAtDateTimestamp || dateOptions[0].value
      ],
      availableTimeSlots.timezone,
    );
  }, [scheduledConsultation, dateOptions, availableTimeSlots]);

  if (scheduledConsultation) {
    return (
      <div className="cur-max-w-[500px] cur-mx-auto cur-py-12 md:cur-py-20">
        <ConsultationCard
          consultation={scheduledConsultation}
          timezone={availableTimeSlots.timezone}
          flowType={flowType}
        />
      </div>
    );
  }

  return (
    <div className="cur-max-w-[500px] cur-py-12 md:cur-py-20 cur-px-4 cur-mx-auto">
      <div className="cur-flex-grow cur-flex cur-items-center">
        <ConsultationForm
          flowType={flowType}
          options={{
            availableTimeSlots,
            dateOptions,
            timeOptions,
          }}
        />
      </div>
    </div>
  );
};
